import { useState } from "react";
import { useAdminSession } from "@/auth/AdminSession";
import ApiService from "@/services/ApiService";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Frame,
  Panel,
  Feedback,
  LoadState,
  useData,
  useMutation,
  fieldClass,
  roles,
  japanDate,
} from "./ui";
type Staff = {
  id: number;
  user_id: number;
  role_key: string;
  status: string;
  user: { name: string; email: string } | null;
};
type Invite = {
  id: number;
  email: string;
  role_key: string;
  expires_at: string;
};
export default function StaffPage() {
  const { can } = useAdminSession();
  const result = useData<{ staff: Staff[]; invitations: Invite[] }>(
    "organization/staff",
  );
  const op = useMutation();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("recruiter");
  const [invitationUrl, setInvitationUrl] = useState("");
  const [edit, setEdit] = useState<Staff | null>(null);
  const [reason, setReason] = useState("");
  if (!can("staff.manage"))
    return (
      <Frame title="スタッフ管理">
        <p>スタッフ管理の権限がありません。</p>
      </Frame>
    );
  return (
    <Frame title="スタッフと招待">
      <Feedback {...op} />
      <Panel title="スタッフを招待">
        <form
          className="flex flex-wrap gap-3 items-end"
          onSubmit={async (e) => {
            e.preventDefault();
            if (
              await op.run(async () => {
                const r = await ApiService.post("organization/invitations", {
                  email,
                  role_key: role,
                });
                setInvitationUrl(r.data.data.invitation_url);
                result.reload();
              }, "招待を作成し、メール送信を予約しました。")
            )
              setEmail("");
          }}
        >
          <label className="flex-grow">
            メールアドレス
            <Input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            ロール
            <select
              className={fieldClass}
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {["organization_admin", "recruiter", "viewer"].map((r) => (
                <option key={r} value={r}>
                  {roles[r]}
                </option>
              ))}
            </select>
          </label>
          <Button disabled={op.busy}>招待</Button>
        </form>
        {invitationUrl && (
          <div className="space-y-2">
            <p className="text-sm">
              招待リンクは7日間有効です。一度参加すると無効になります。
            </p>
            <Input readOnly value={invitationUrl} />
            <Button
              variant="outline"
              onClick={() => navigator.clipboard.writeText(invitationUrl)}
            >
              リンクをコピー
            </Button>
          </div>
        )}
      </Panel>
      <Panel title="スタッフ">
        <LoadState {...result} />
        {result.data?.staff.map((s) => (
          <div
            key={s.id}
            className="flex flex-wrap gap-3 justify-between border-b py-3"
          >
            <div>
              <p className="font-medium">
                {s.user?.name ?? "削除済みユーザー"}
              </p>
              <p className="text-sm">
                {s.user?.email} · {roles[s.role_key]} ·{" "}
                {s.status === "active" ? "有効" : "停止"}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setEdit(s);
                setReason("");
              }}
            >
              変更
            </Button>
          </div>
        ))}
      </Panel>
      {edit && (
        <Panel title="ロール・利用状態の変更">
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              if (
                await op.run(async () => {
                  await ApiService.patch("organization/staff/" + edit.id, {
                    role_key: edit.role_key,
                    status: edit.status,
                    reason,
                  });
                  result.reload();
                })
              )
                setEdit(null);
            }}
          >
            <p>{edit.user?.name}</p>
            <label className="block">
              ロール
              <select
                className={fieldClass}
                value={edit.role_key}
                onChange={(e) => setEdit({ ...edit, role_key: e.target.value })}
              >
                {["organization_admin", "recruiter", "viewer"].map((r) => (
                  <option key={r} value={r}>
                    {roles[r]}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              状態
              <select
                className={fieldClass}
                value={edit.status}
                onChange={(e) => setEdit({ ...edit, status: e.target.value })}
              >
                <option value="active">有効</option>
                <option value="suspended">停止</option>
              </select>
            </label>
            <label className="block">
              変更理由
              <Input
                required
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <div className="flex gap-3">
              <Button disabled={op.busy}>保存</Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEdit(null)}
              >
                キャンセル
              </Button>
            </div>
          </form>
        </Panel>
      )}
      <Panel title="参加待ちの招待">
        {result.data?.invitations.map((i) => (
          <div
            key={i.id}
            className="flex flex-wrap justify-between gap-3 border-b py-3"
          >
            <div>
              <p>
                {i.email} · {roles[i.role_key]}
              </p>
              <p className="text-sm">有効期限 {japanDate(i.expires_at)}</p>
            </div>
            <Button
              variant="outline"
              disabled={op.busy}
              onClick={() =>
                op.run(async () => {
                  await ApiService.delete("organization/invitations/" + i.id);
                  result.reload();
                }, "招待を取り消しました。")
              }
            >
              取り消す
            </Button>
          </div>
        ))}
        {result.data && !result.data.invitations.length && (
          <p>参加待ちの招待はありません。</p>
        )}
      </Panel>
    </Frame>
  );
}
